import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { QueryClient } from 'react-query';

import axiosClient from '../../api/axios-client';
import type { ApiError } from '../../api/useApi';
import type {
    HousekeepingRoomStatus,
    IssueComment,
    IssueStatus,
    TechnicalIssue,
    TechnicalIssueDetail,
} from '../../pages/housekeeping/types/housekeeping';
import { compressImageWithDetails } from '../../utils/imageCompression';
import { parametresOnglet } from '../helpers/maintenance';
import type { OngletMaintenance } from '../helpers/maintenance';

/**
 * Les lectures et gestes de l'écran « Maintenance » du technicien.
 *
 * Mêmes appels que le web (`useTechnicalIssues`, `useTechnicalIssue`, `useTakeIssue`,
 * `useUpdateTechnicalIssue`, `useAddIssueComment`), recopiés pour la frontière du
 * socle : seuls les TYPES viennent de `pages/`, en `import type`.
 *
 * L'établissement est dans la clé, comme « Mes chambres » : une bascule d'hôtel
 * ne sert jamais une seconde les pannes du précédent.
 */

const URL_PANNES = '/housekeeping/technical-issues';

/** Le web sonde ses listes ménage toutes les 60 s ; même rythme ici. */
export const SONDAGE_PANNES_MS = 60_000;

interface PagePannes {
    data: TechnicalIssue[];
}

export function cleListePannes(facilityId: number | null, onglet: OngletMaintenance) {
    return [URL_PANNES, { facilityId, onglet }] as const;
}

export function usePannes(facilityId: number | null, onglet: OngletMaintenance) {
    return useQuery<TechnicalIssue[]>(
        cleListePannes(facilityId, onglet),
        () =>
            axiosClient
                .get<PagePannes>(`${URL_PANNES}?${parametresOnglet(onglet)}`)
                .then((res) => res.data.data),
        { enabled: facilityId !== null, refetchInterval: SONDAGE_PANNES_MS },
    );
}

export function usePanne(facilityId: number | null, id: number) {
    return useQuery<TechnicalIssueDetail>(
        [`${URL_PANNES}/${id}`, { facilityId }],
        () => axiosClient.get<TechnicalIssueDetail>(`${URL_PANNES}/${id}`).then((res) => res.data),
        { enabled: facilityId !== null && Number.isFinite(id), refetchInterval: SONDAGE_PANNES_MS },
    );
}

/** Les chambres et espaces de l'hôtel, pour « Signaler » (lisible avec `housekeeping.view`). */
export function useLieuxDeLHotel(facilityId: number | null, actif: boolean) {
    return useQuery<HousekeepingRoomStatus[]>(
        ['/housekeeping/board', { facilityId }],
        () => axiosClient.get<HousekeepingRoomStatus[]>('/housekeeping/board').then((res) => res.data),
        { enabled: facilityId !== null && actif, staleTime: 5 * 60 * 1000 },
    );
}

/** Toute la maintenance : listes des trois onglets et fiches. */
export function invaliderPannes(queryClient: QueryClient): void {
    void queryClient.invalidateQueries({
        predicate: (query) => {
            const cle = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
            return typeof cle === 'string' && cle.startsWith(URL_PANNES);
        },
    });
}

/**
 * Erreur HTTP avec son statut : 409 de « Je m'en occupe » (quelqu'un l'a pris
 * entre-temps, `assigned_to_name`), 422 d'une transition refusée.
 */
export interface ErreurPrise extends ApiError {
    response?: {
        status?: number;
        data: { message: string; errors: Record<string, string[]>; assigned_to_name?: string | null };
    };
}

/** « Je m'en occupe ». Succès comme échec rafraîchissent : un 409 doit montrer le preneur. */
export function usePrendrePanne() {
    const queryClient = useQueryClient();
    return useMutation<TechnicalIssue, ErreurPrise, { id: number }>(
        ({ id }) => axiosClient.post(`${URL_PANNES}/${id}/take`).then((res) => res.data),
        { onSettled: () => invaliderPannes(queryClient) },
    );
}

export function useChangerStatutPanne() {
    const queryClient = useQueryClient();
    return useMutation<TechnicalIssue, ErreurPrise, { id: number; status: IssueStatus }>(
        ({ id, status }) => axiosClient.put(`${URL_PANNES}/${id}`, { status }).then((res) => res.data),
        { onSettled: () => invaliderPannes(queryClient) },
    );
}

export interface NouveauCommentaire {
    issueId: number;
    content: string;
    photo?: File | null;
}

export function useCommenterPanne() {
    const queryClient = useQueryClient();
    return useMutation<IssueComment, ApiError, NouveauCommentaire>(
        async ({ issueId, content, photo }) => {
            const formData = new FormData();
            formData.append('content', content);
            if (photo) {
                // Le serveur plafonne à 5 Mo par image : une photo de téléphone dépasse souvent.
                const { file } = await compressImageWithDetails(photo);
                formData.append('photos[0]', file);
            }
            const res = await axiosClient.post(`${URL_PANNES}/${issueId}/comments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data;
        },
        { onSuccess: () => invaliderPannes(queryClient) },
    );
}
